import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  BadgeCheck,
  Check,
  Clock,
  FlaskConical,
  Lock,
  Radio,
  Send,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { FieldSelectFullWidth } from "@/components/trade-filter-selects";
import { cn } from "@/lib/utils";
import { presentMutationError } from "../../../utils/showApiErrorToast";

import { listReportStrategies, listReportStrategyPolicyVersions } from "../../../services/reportsApi";
import type { ReportStrategyPolicyVersion, ReportStrategyResponse } from "../../../types/reports";
import { createDeployment, listLabStrategies } from "../../../services/strategyDeployApi";
import type { StrategyRdHypothesis } from "../../../types/strategyRd";
import type { DeployMode, DeploySetup, DeploySource, Deployment } from "../../../types/strategyDeploy";
import { ReportsMetaIconChip } from "../Reports/components/ReportsCardChrome";
import { ConnectTelegramButton } from "./ConnectTelegramButton";
import { familyOf } from "./ComparePanel";
import { DAYS, DayPicker, Field, SettingRow, SizingTabs } from "./DeploymentSettingsDialog";
import { DeployDisclaimer } from "./disclaimer";
import { BROKER_LABEL, MODE_BLURB, MODE_LABEL, fmtPct } from "./liveStrategiesFormat";
import {
  MODE_ICON,
  liveBodyTextClassName,
  liveCardBodyClassName,
  liveCardClassName,
  liveCardTitleClassName,
  liveFigureClassName,
  liveInsetClassName,
  liveLabelClassName,
  liveOptionTitleClassName,
  liveSectionTitleClassName,
  liveSmallTextClassName,
} from "./liveStrategiesUi";
import { LiveBadge, LiveIconTile } from "./LiveUiParts";
import { useAccountLabel } from "./useAccountLabel";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setup: DeploySetup | null;
  /** Pre-selected Lab strategy (from the Lab's "Launch" button). */
  hypothesisId?: string | null;
  /** Pre-selected Strategies-page strategy / policy version (its "Deploy" buttons). */
  reportStrategyId?: string | null;
  policyVersionId?: string | null;
  onCreated: (dep: Deployment) => void;
  /** Re-read accounts / Telegram link (after connecting Telegram in another tab). */
  onRefreshSetup: () => void;
  /** The user's deployments — shows what already runs for the chosen strategy. */
  deployments?: Deployment[];
};

const STEPS = ["Strategy", "How it trades", "Account", "Risk", "Review"] as const;
const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1h", "4h", "1d"];

function num(v: string): number | null {
  const n = Number(v);
  return v.trim() !== "" && Number.isFinite(n) ? n : null;
}

function strategyTag(h: StrategyRdHypothesis): string {
  const ctx = (h.scan_context ?? {}) as unknown as Record<string, unknown>;
  const version = (h as { version?: number | null }).version;
  return [String(ctx.symbol ?? ""), String(ctx.candle_type ?? ""), version ? `v${version}` : ""]
    .filter(Boolean)
    .join(" · ");
}

/** One step section: the Statistics card shell with its chart-card title. */
function WizardSection({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className={cn(liveCardClassName, liveCardBodyClassName)}>
      <h3 className={liveCardTitleClassName}>{title}</h3>
      {children}
    </section>
  );
}

/** A choice (source, mode) as a selectable card: brand icon tile, brand title, muted blurb. */
function OptionCard({
  selected,
  disabled,
  icon,
  title,
  blurb,
  footer,
  onSelect,
  ...rest
}: {
  selected: boolean;
  disabled?: boolean;
  icon: LucideIcon;
  title: string;
  blurb: string;
  footer?: React.ReactNode;
  onSelect: () => void;
} & Record<`data-${string}`, string>): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-disabled={disabled || undefined}
      className={cn(
        liveCardClassName,
        "h-full items-start gap-3 p-5 text-left transition-colors",
        selected
          ? "!border-[color:var(--brand-tropical-surf)] ring-1 ring-[color:var(--brand-tropical-surf)]"
          : "hover:!border-[color:var(--brand-tropical-surf)]/60",
        disabled && "cursor-not-allowed opacity-60"
      )}
      {...rest}
    >
      <span className="flex w-full items-start justify-between gap-2">
        <LiveIconTile icon={icon} />
        {selected ? (
          <LiveBadge tone="brand" icon={Check}>
            Selected
          </LiveBadge>
        ) : null}
      </span>
      <span className={liveOptionTitleClassName}>{title}</span>
      <span className={liveSmallTextClassName}>{blurb}</span>
      {footer ? <span className="mt-auto pt-1">{footer}</span> : null}
    </button>
  );
}

/** The chosen strategy's backtest at a glance: what live trading will be compared against. */
function BacktestSummary({ h }: { h: StrategyRdHypothesis }) {
  const ctx = (h.scan_context ?? {}) as unknown as Record<string, unknown>;
  const ov = (h.last_apply?.overview ?? {}) as unknown as Record<string, number | null>;
  const cells: [string, string][] = [
    ["Market", `${String(ctx.symbol ?? "N/A")}, ${String(ctx.candle_type ?? "N/A")}`],
    ["Trades", ov.closed_count != null ? String(ov.closed_count) : "N/A"],
    ["Won", ov.win_rate != null ? fmtPct(ov.win_rate) : "N/A"],
    ["Profit factor", ov.profit_factor_r != null ? Number(ov.profit_factor_r).toFixed(2) : "N/A"],
  ];
  return (
    <div className={cn(liveInsetClassName, "mt-1 grid grid-cols-2 gap-4 bg-muted/30 p-4 sm:grid-cols-4")} data-live-backtest-summary="">
      {cells.map(([k, v]) => (
        <div key={k} className="flex min-w-0 flex-col gap-1">
          <span className={liveLabelClassName}>{k}</span>
          <span className={cn(liveFigureClassName, "truncate")}>{v}</span>
        </div>
      ))}
    </div>
  );
}

export function DeployWizard({
  open,
  onOpenChange,
  setup,
  hypothesisId,
  reportStrategyId,
  policyVersionId,
  onCreated,
  onRefreshSetup,
  deployments = [],
}: Props): JSX.Element {
  const accountName = useAccountLabel();
  const [step, setStep] = useState(0);
  const [source, setSource] = useState<DeploySource>("lab");
  /** "strategies": a Strategies-page strategy + policy version (entries from its Lab rules). */
  const [pick, setPick] = useState<"lab" | "strategies" | "pine">("lab");
  const [strategies, setStrategies] = useState<ReportStrategyResponse[] | null>(null);
  const [strategyId, setStrategyId] = useState<string>("");
  const [versions, setVersions] = useState<ReportStrategyPolicyVersion[] | null>(null);
  const [versionId, setVersionId] = useState<string>("");
  const [hyps, setHyps] = useState<StrategyRdHypothesis[] | null>(null);
  const [hypId, setHypId] = useState<string>(hypothesisId ?? "");
  const [name, setName] = useState("");
  const [pineSymbol, setPineSymbol] = useState("XAUUSD");
  const [pineTf, setPineTf] = useState("15m");
  const [pineScript, setPineScript] = useState("");
  const [pineStop, setPineStop] = useState("");
  const [mode, setMode] = useState<DeployMode>("paper");
  const [account, setAccount] = useState("");
  const [sizing, setSizing] = useState<"risk_pct" | "fixed">("risk_pct");
  const [riskPct, setRiskPct] = useState("0.5");
  const [fixedQty, setFixedQty] = useState("0.1");
  const [maxQty, setMaxQty] = useState("1");
  const [dailyLoss, setDailyLoss] = useState("3");
  const [maxOpen, setMaxOpen] = useState("1");
  const [maxDay, setMaxDay] = useState("3");
  const [ttl, setTtl] = useState("2");
  const [telegram, setTelegram] = useState(true);
  const [useHours, setUseHours] = useState(false);
  const [days, setDays] = useState<number[]>([0, 1, 2, 3, 4]);
  const [start, setStart] = useState("07:00");
  const [end, setEnd] = useState("17:00");
  const [paperEquity, setPaperEquity] = useState("10000");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setAccept(false);
    if (hypothesisId) {
      setSource("lab");
      setPick("lab");
      setHypId(hypothesisId);
    }
    if (reportStrategyId) {
      // Preloaded from a workspace policy (its strategy was saved from the Strategy Lab).
      setSource("lab");
      setPick("strategies");
      setStrategyId(reportStrategyId);
      setVersionId(policyVersionId ?? "");
    }
    const ctrl = new AbortController();
    listLabStrategies(ctrl.signal)
      .then((rows) => setHyps(rows))
      .catch(() => setHyps([]));
    listReportStrategies()
      .then((r) => setStrategies((r.strategies ?? []).filter((x) => !(x as { is_archived?: boolean }).is_archived)))
      .catch(() => setStrategies([]));
    return () => ctrl.abort();
  }, [open, hypothesisId, reportStrategyId, policyVersionId]);

  // Policy versions of the chosen Strategies-page strategy (current one preselected).
  useEffect(() => {
    if (!open || !strategyId) {
      setVersions(null);
      return;
    }
    let alive = true;
    listReportStrategyPolicyVersions(strategyId)
      .then((r) => {
        if (!alive) return;
        const rows = (r.versions ?? []).filter((v) => !v.is_archived);
        setVersions(rows);
        setVersionId((prev) => {
          if (prev && rows.some((v) => v.id === prev)) return prev;
          const cur = (strategies ?? []).find((x) => x.id === strategyId) as { current_policy_version_id?: string } | undefined;
          return cur?.current_policy_version_id && rows.some((v) => v.id === cur.current_policy_version_id)
            ? cur.current_policy_version_id
            : rows[rows.length - 1]?.id ?? "";
        });
      })
      .catch(() => alive && setVersions([]));
    return () => {
      alive = false;
    };
  }, [open, strategyId, strategies]);

  const tested = useMemo(() => (hyps ?? []).filter((h) => h.last_apply), [hyps]);
  const hyp = tested.find((h) => h.id === hypId) ?? (hyps ?? []).find((h) => h.id === hypId) ?? null;
  const accounts = setup?.accounts ?? [];
  const acct = accounts.find((a) => `${a.broker_name}|${a.account_code}` === account) ?? null;
  const live = mode !== "paper";
  /** "limit" / "stop" when the strategy enters with a resting order (live sends it at the close). */
  const restingOrder = ((): string | null => {
    const entry = ((hyp as { template?: { entry?: { mode?: string; rule?: { order?: string } } } } | null)?.template
      ?.entry ?? {}) as { mode?: string; rule?: { order?: string } };
    if (entry.mode === "limit_at_level") return "limit";
    if (entry.mode === "rule" && (entry.rule?.order === "limit" || entry.rule?.order === "stop")) return entry.rule.order;
    return null;
  })();
  const plan = setup?.plan;
  const family = hyp ? String((hyp as { root_id?: string | null }).root_id || hyp.id) : null;
  const running = deployments.filter((d) => d.status === "running" || d.status === "paused");
  const siblings = family ? running.filter((d) => familyOf(d) === family) : [];
  /** Why the plan doesn't allow this mode now, or null. */
  const modeBlock = (m: DeployMode): string | null => {
    if (!plan || plan.admin) return null;
    if (m === "auto" && !plan.auto) return `Not on ${plan.name}`;
    if (m !== "paper" && plan.usage.live >= plan.live_max) return `${plan.usage.live}/${plan.live_max} live in use`;
    return null;
  };
  const planBlock = ((): string | null => {
    if (!plan || plan.admin) return null;
    if (plan.usage.running >= plan.running_max)
      return `${plan.name} runs up to ${plan.running_max} strategies at once. Stop one first, or upgrade.`;
    if (source === "pine" && !plan.pine) return `${plan.name} doesn't include TradingView strategies.`;
    if (siblings.length >= plan.per_strategy_max)
      return `${plan.name} runs up to ${plan.per_strategy_max} copies of one strategy side by side.`;
    return null;
  })();

  const stepError = useMemo((): string | null => {
    if (step === 0) {
      if (planBlock) return planBlock;
      if (pick === "strategies") {
        if (!strategyId) return "Choose a strategy.";
        if (!versionId) return "Choose the policy version to run.";
      } else if (source === "lab" && !hyp) return "Choose a backtested strategy.";
      if (source === "lab" && hyp && !hyp.last_apply) return "Run a backtest of this strategy first.";
      if (source === "pine" && !pineSymbol.trim()) return "Enter the market.";
    }
    if (step === 1 && modeBlock(mode)) return `${MODE_LABEL[mode]}: ${modeBlock(mode)}.`;
    if (step === 2 && live && !acct) return "Choose the account to trade on.";
    if (step === 3) {
      if (sizing === "risk_pct" && !(num(riskPct)! > 0)) return "Risk per trade must be above 0%.";
      if (sizing === "risk_pct" && num(riskPct)! > (setup?.max_risk_pct ?? 5))
        return `Risk per trade is capped at ${setup?.max_risk_pct ?? 5}%.`;
      if (sizing === "fixed" && !(num(fixedQty)! > 0)) return "Enter a position size.";
      if (live && !(num(maxQty)! > 0)) return "Set a maximum position size.";
      if (useHours && !days.length) return "Pick at least one trading day.";
    }
    if (step === 4 && live && !accept) return "Read and accept the terms to trade on your account.";
    return null;
  }, [step, source, hyp, pineSymbol, live, acct, sizing, riskPct, fixedQty, maxQty, useHours, days, mode, accept, setup, planBlock]);

  const submit = async () => {
    setBusy(true);
    try {
      const dep = await createDeployment({
        source,
        hypothesis_id: pick === "lab" ? hypId : undefined,
        report_strategy_id: pick === "strategies" ? strategyId : undefined,
        policy_version_id: pick === "strategies" ? versionId : undefined,
        name: name.trim() || (pick === "lab" ? hyp?.name : pick === "pine" ? "TradingView alerts" : undefined) || undefined,
        symbol: source === "pine" ? pineSymbol.trim().toUpperCase() : undefined,
        candle_type: source === "pine" ? pineTf : undefined,
        script: source === "pine" ? pineScript : undefined,
        mode,
        broker_name: live ? acct?.broker_name : null,
        broker_account_code: live ? acct?.account_code : null,
        accept_risk_terms: live ? accept : undefined,
        config: {
          risk: {
            sizing,
            risk_pct: sizing === "risk_pct" ? num(riskPct) : null,
            fixed_quantity: sizing === "fixed" ? num(fixedQty) : null,
            max_quantity: num(maxQty),
            max_daily_loss_pct: num(dailyLoss) ?? 3,
            max_open_trades: num(maxOpen) ?? 1,
            max_trades_per_day: num(maxDay) ?? 3,
          },
          schedule: useHours ? { days, start, end, tz } : null,
          signal_ttl_bars: num(ttl) ?? 2,
          notify: { telegram },
          paper: { equity: num(paperEquity) ?? 10000 },
          pine: source === "pine" && num(pineStop) ? { stop_distance: num(pineStop) } : undefined,
        },
      });
      toast.success(`${dep.name} launched`, { description: `${MODE_LABEL[dep.mode]} on ${dep.symbol} ${dep.candle_type}` });
      onCreated(dep);
      onOpenChange(false);
    } catch (e) {
      presentMutationError(e, "Could not deploy the strategy");
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (stepError) {
      toast.error(stepError);
      return;
    }
    if (step === 1 && !live) setStep(3);
    else if (step < STEPS.length - 1) setStep(step + 1);
    else void submit();
  };
  const back = () => setStep(step === 3 && !live ? 1 : Math.max(0, step - 1));

  const strategyOptions = (strategies ?? []).map((st) => ({ value: st.id, label: st.name }));
  const versionOptions = (versions ?? []).map((v) => ({
    value: v.id,
    label: `v${v.version_number ?? "?"}`,
    description: (v as { title?: string | null }).title || undefined,
  }));
  // A Lab strategy = a family of backtested versions (root_id); newest version first.
  const families = useMemo(() => {
    const by = new Map<string, StrategyRdHypothesis[]>();
    for (const h of tested) {
      const key = h.root_id || h.id;
      by.set(key, [...(by.get(key) ?? []), h]);
    }
    return [...by.entries()]
      .map(([key, versions]) => ({
        key,
        versions: [...versions].sort((x, y) => (y.version ?? 0) - (x.version ?? 0)),
      }))
      .sort((x, y) => String(y.versions[0].created_at ?? "").localeCompare(String(x.versions[0].created_at ?? "")));
  }, [tested]);
  const familyKey = hyp ? hyp.root_id || hyp.id : "";
  const familyOptions = families.map((f) => ({
    value: f.key,
    label: f.versions[0].name,
    description: strategyTag(f.versions[0]) || undefined,
  }));
  const versionOfFamilyOptions = (families.find((f) => f.key === familyKey)?.versions ?? []).map((h) => ({
    value: h.id,
    label: `v${h.version ?? 1}${h.name !== families.find((f) => f.key === familyKey)?.versions[0].name ? ` · ${h.name}` : ""}`,
    description: h.created_at ? new Date(h.created_at).toLocaleString() : undefined,
  }));
  const accountOptions = accounts.map((a) => ({
    value: `${a.broker_name}|${a.account_code}`,
    label: `${BROKER_LABEL[a.broker_name] ?? a.broker_name}: ${accountName(a.broker_name, a.account_code, a.label)}`,
  }));
  const reviewRows: [string, string][] = [
    [
      "Strategy",
      (pick === "strategies"
        ? `${(strategies ?? []).find((x) => x.id === strategyId)?.name ?? "Strategy"}, policy v${versions?.find((v) => v.id === versionId)?.version_number ?? "?"}`
        : source === "lab"
          ? hyp?.name
          : `${pineSymbol.toUpperCase()} ${pineTf}, TradingView`) || "N/A",
    ],
    ["Mode", MODE_LABEL[mode]],
    [
      "Account",
      live ? `${BROKER_LABEL[acct?.broker_name ?? ""] ?? ""}: ${accountName(acct?.broker_name, acct?.account_code, acct?.label)}` : "Paper",
    ],
    ["Size", `${sizing === "risk_pct" ? `${riskPct}% risk per trade` : `${fixedQty} lots`}${num(maxQty) ? `, max ${maxQty} lots` : ""}`],
    ["Limits", `${maxOpen} open, ${maxDay} a day, stop for the day at −${dailyLoss}%`],
    ["Hours", useHours ? `${days.map((d) => DAYS[d]).join(" ")}, ${start} to ${end}` : "Whenever the market is open"],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] gap-4 overflow-y-auto p-6 sm:max-w-[820px]" data-live-deploy-wizard="" data-live-ui="">
        <DialogHeader className="gap-1 pr-8">
          <DialogTitle className={cn(liveSectionTitleClassName, "text-xl")}>Launch a strategy</DialogTitle>
          <DialogDescription className={liveBodyTextClassName}>
            Run one of your own strategies on live prices, on paper or on your broker account.
          </DialogDescription>
          {plan && !plan.admin ? (
            <span className="flex flex-wrap items-center gap-1.5 pt-1" data-live-wizard-plan="">
              <ReportsMetaIconChip id="wizard-plan" label="Plan" value={plan.name} icon={BadgeCheck} />
              <ReportsMetaIconChip
                id="wizard-plan-running"
                label="Running"
                value={`${plan.usage.running} of ${plan.running_max} running`}
                icon={Activity}
              />
              <ReportsMetaIconChip id="wizard-plan-live" label="Live on a broker" value={`${plan.usage.live} of ${plan.live_max} live`} icon={Radio} />
            </span>
          ) : null}
        </DialogHeader>

        <ol className="!m-0 flex !list-none items-start gap-2 !p-0" data-live-wizard-steps="">
          {STEPS.map((label, i) => {
            const skipped = !live && i === 2;
            const done = i < step;
            const active = i === step;
            return (
              <li key={label} className={cn("flex flex-1 flex-col gap-1.5", skipped && "opacity-40")} aria-current={active ? "step" : undefined}>
                <span
                  className={cn(
                    "h-1 rounded-full transition-colors",
                    active || done ? "bg-[image:var(--gradient-primary)]" : "bg-muted"
                  )}
                />
                <span
                  className={cn(
                    "font-[family-name:var(--font-primary)] text-xs",
                    active ? "font-medium text-[color:var(--brand-tropical-surf)]" : "text-gray-500 dark:text-white"
                  )}
                >
                  {label}
                </span>
              </li>
            );
          })}
        </ol>

        <div className="flex min-h-[300px] flex-col gap-4">
          {step === 0 ? (
            <>
              {/* Strategy Lab only: its backtested rules are what Launchpad executes. Strategies-page
                  policies and TradingView alerts come back once they have executable rules. */}
              <WizardSection title="Strategy Lab strategy">
                {pick === "strategies" ? (
                  <div className="flex flex-col gap-1" data-live-preloaded-policy="">
                    <span className={liveOptionTitleClassName}>
                      {(strategies ?? []).find((x) => x.id === strategyId)?.name ?? "Strategy"}
                      {versions?.find((v) => v.id === versionId)?.version_number != null
                        ? ` · policy v${versions?.find((v) => v.id === versionId)?.version_number}`
                        : ""}
                    </span>
                    <span className={liveSmallTextClassName}>
                      Entries come from its Strategy Lab rules; this policy version adds its filters and stop / target
                      rules on top.
                    </span>
                  </div>
                ) : (
                  <>
                  <Field label="Strategy">
                    <FieldSelectFullWidth
                      value={familyKey}
                      onValueChange={(v) => setHypId(families.find((f) => f.key === v)?.versions[0]?.id ?? "")}
                      options={familyOptions}
                      emptyValueShowsPlaceholder
                      placeholder={hyps ? (families.length ? "Choose a strategy" : "No backtested strategies yet") : "Loading…"}
                    />
                  </Field>
                  <Field label="Version" hint="Live signals come from exactly the same rules as this backtest.">
                    <FieldSelectFullWidth
                      value={hypId}
                      onValueChange={setHypId}
                      options={versionOfFamilyOptions}
                      disabled={!familyKey}
                      emptyValueShowsPlaceholder
                      placeholder={familyKey ? "Choose a version" : "Choose a strategy first"}
                    />
                    {hyp ? <BacktestSummary h={hyp} /> : null}
                    {siblings.length ? (
                      <span className={cn(liveSmallTextClassName, "flex flex-wrap items-center gap-1.5")} data-live-siblings="">
                        Already running:
                        {siblings.map((d) => (
                          <LiveBadge key={d.id} tone="brand" icon={MODE_ICON[d.mode]}>
                            {MODE_LABEL[d.mode]}
                            {(d.strategy_snapshot?.backtest as { version?: number } | undefined)?.version
                              ? `, v${(d.strategy_snapshot?.backtest as { version?: number }).version}`
                              : ""}
                          </LiveBadge>
                        ))}
                        <span>This one runs alongside and shows up in Side by side.</span>
                      </span>
                    ) : null}
                  </Field>
                  </>
                )}
              </WizardSection>
            </>
          ) : null}

          {step === 1 ? (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                {(["paper", "approve", "auto"] as DeployMode[]).map((m) => {
                  const blocked = modeBlock(m);
                  return (
                    <OptionCard
                      key={m}
                      selected={mode === m}
                      disabled={!!blocked}
                      icon={MODE_ICON[m]}
                      title={MODE_LABEL[m]}
                      blurb={MODE_BLURB[m]}
                      onSelect={() => !blocked && setMode(m)}
                      footer={
                        blocked ? (
                          <LiveBadge tone="neutral" icon={Lock}>
                            {blocked}
                          </LiveBadge>
                        ) : m === "paper" ? (
                          <LiveBadge tone="good">Recommended first</LiveBadge>
                        ) : m === "approve" ? (
                          <LiveBadge tone="brand">You decide each trade</LiveBadge>
                        ) : (
                          <LiveBadge tone="warn">Trades without asking</LiveBadge>
                        )
                      }
                      data-live-mode={m}
                    />
                  );
                })}
              </div>
              {live && restingOrder ? (
                <WizardSection title="How entries are sent">
                  <p className={cn(liveSmallTextClassName, "m-0")}>
                    This strategy enters with a {restingOrder} order. On your account the order is sent at market when the
                    candle that reaches the level closes, and skipped if price has already moved more than a quarter of
                    the stop distance past it. Paper fills at the level, as in the backtest.
                  </p>
                </WizardSection>
              ) : null}
            </>
          ) : null}

          {step === 2 ? (
            <WizardSection title="Broker account">
              {accounts.length ? (
                <Field label="Account to trade on">
                  <FieldSelectFullWidth
                    value={account}
                    onValueChange={setAccount}
                    options={accountOptions}
                    emptyValueShowsPlaceholder
                    placeholder="Choose an account"
                  />
                </Field>
              ) : (
                <div className={cn(liveInsetClassName, "!border-dashed p-5")}>
                  <span className={liveBodyTextClassName}>
                    No Liquid Charts or cTrader account is connected.{" "}
                    <a className="font-medium text-[color:var(--brand-tropical-surf)] underline" href="/profile/broker-api/connect">
                      Connect a broker
                    </a>{" "}
                    or go back and choose Paper.
                  </span>
                </div>
              )}
              {acct?.broker_name === "ctrader" ? (
                <p className={cn(liveSmallTextClassName, "m-0")}>
                  cTrader needs trading permission: if orders are refused, reconnect cTrader from your profile.
                </p>
              ) : null}
            </WizardSection>
          ) : null}

          {step === 3 ? (
            <>
              <WizardSection title="Size and limits">
                <SizingTabs value={sizing} onChange={setSizing} />
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {sizing === "risk_pct" ? (
                    <Field label="Risk per trade (%)" hint={`Max ${setup?.max_risk_pct ?? 5}%`}>
                      <Input value={riskPct} onChange={(e) => setRiskPct(e.target.value)} inputMode="decimal" />
                    </Field>
                  ) : (
                    <Field label="Position size (lots)">
                      <Input value={fixedQty} onChange={(e) => setFixedQty(e.target.value)} inputMode="decimal" />
                    </Field>
                  )}
                  <Field label="Never larger than (lots)" hint={live ? "Required" : "Optional on paper"}>
                    <Input value={maxQty} onChange={(e) => setMaxQty(e.target.value)} inputMode="decimal" />
                  </Field>
                  <Field label="Daily loss limit (%)">
                    <Input value={dailyLoss} onChange={(e) => setDailyLoss(e.target.value)} inputMode="decimal" />
                  </Field>
                  <Field label="Max open trades">
                    <Input value={maxOpen} onChange={(e) => setMaxOpen(e.target.value)} inputMode="numeric" />
                  </Field>
                  <Field label="Max trades a day">
                    <Input value={maxDay} onChange={(e) => setMaxDay(e.target.value)} inputMode="numeric" />
                  </Field>
                  {mode === "approve" ? (
                    <Field label="Signal expires after (candles)">
                      <Input value={ttl} onChange={(e) => setTtl(e.target.value)} inputMode="numeric" />
                    </Field>
                  ) : null}
                  {mode === "paper" ? (
                    <Field label="Paper account ($)">
                      <Input value={paperEquity} onChange={(e) => setPaperEquity(e.target.value)} inputMode="decimal" />
                    </Field>
                  ) : null}
                </div>
              </WizardSection>
              <div className={cn(liveCardClassName, "overflow-hidden")}>
                <SettingRow
                  icon={Clock}
                  title="Trading hours"
                  description={`Only take new trades inside these hours (${tz}).`}
                  control={<Switch checked={useHours} onCheckedChange={setUseHours} aria-label="Trading hours" />}
                >
                  {useHours ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <DayPicker days={days} onChange={setDays} />
                      <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="w-28" />
                      <span className={liveSmallTextClassName}>to</span>
                      <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="w-28" />
                    </div>
                  ) : null}
                </SettingRow>
                <SettingRow
                  className="!border-0 !border-t !border-solid !border-border"
                  icon={Send}
                  title="Telegram"
                  badge={
                    <LiveBadge tone={setup?.telegram_linked ? "good" : "warn"}>
                      {setup?.telegram_linked ? "Connected" : "Not connected"}
                    </LiveBadge>
                  }
                  description={
                    setup?.telegram_linked
                      ? mode === "approve"
                        ? "Signals arrive with Approve and Reject buttons."
                        : "Get every signal, fill and close."
                      : mode === "approve"
                        ? "Connect Telegram to approve signals from your phone."
                        : "Connect Telegram to get every signal, fill and close."
                  }
                  control={
                    setup?.telegram_linked ? (
                      <Switch checked={telegram} onCheckedChange={setTelegram} aria-label="Send to Telegram" />
                    ) : (
                      <ConnectTelegramButton onReturn={onRefreshSetup} size="sm" variant="gradient" label="Connect" />
                    )
                  }
                />
              </div>
            </>
          ) : null}

          {step === 4 ? (
            <>
              <WizardSection title="Review">
                <dl className="!m-0 grid grid-cols-[7rem_minmax(0,1fr)] gap-x-6 gap-y-3" data-live-wizard-review="">
                  {reviewRows.map(([k, v]) => (
                    <React.Fragment key={k}>
                      <dt className={cn(liveLabelClassName, "self-center")}>{k}</dt>
                      <dd className="!m-0 font-[family-name:var(--font-primary)] text-sm text-gray-500 dark:text-white">{v}</dd>
                    </React.Fragment>
                  ))}
                </dl>
                {source === "pine" ? (
                  <p className={cn(liveSmallTextClassName, "m-0")}>
                    After deploying you'll get a webhook URL and the alert message to paste into your TradingView alert.
                  </p>
                ) : null}
              </WizardSection>
              {live ? (
                <div className={cn(liveCardClassName, "flex-row items-start gap-3 p-5")}>
                  <LiveIconTile icon={ShieldCheck} />
                  <span className="flex min-w-0 flex-col gap-1">
                    <span className={liveOptionTitleClassName}>Protected at the broker</span>
                    <span className={liveSmallTextClassName}>
                      Every order carries the strategy's stop and target at the broker, so a trade stays protected even if
                      Tessera is unreachable. The kill switch closes everything at once.
                    </span>
                  </span>
                </div>
              ) : null}
              {live ? (
                <div className={cn(liveCardClassName, liveCardBodyClassName, "!border-[color:var(--brand-glacial-red)]")} data-live-wizard-terms="">
                  <span className={liveOptionTitleClassName}>Before you trade on your account</span>
                  <DeployDisclaimer />
                  <label className={cn(liveSmallTextClassName, "flex cursor-pointer items-start gap-3")}>
                    <Checkbox checked={accept} onCheckedChange={(v) => setAccept(v === true)} className="mt-0.5" />
                    <span>
                      I've read this. This is my own strategy, deployed by me on my account, and I'm responsible for the
                      orders it places{mode === "auto" ? ", including the ones sent without asking me" : ""}.
                      <span className="text-[color:var(--brand-tropical-surf)]"> (Terms {setup?.risk_terms_version ?? "N/A"})</span>
                    </span>
                  </label>
                </div>
              ) : (
                <DeployDisclaimer compact />
              )}
            </>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2 !border-0 !border-t !border-solid !border-border pt-4">
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            data-font="primary"
            className="h-8 px-4 shadow-none"
            onClick={step === 0 ? () => onOpenChange(false) : back}
          >
            <span className="normal-case tracking-normal">{step === 0 ? "Cancel" : "Back"}</span>
          </Button>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 px-4 shadow-none"
            onClick={next}
            disabled={busy}
            data-live-deploy-next=""
          >
            <span className="normal-case tracking-normal">
              {step === STEPS.length - 1 ? (busy ? "Deploying…" : mode === "paper" ? "Start paper trading" : "Launch") : "Continue"}
            </span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
